import React, { useState, useRef, useEffect } from 'react';
import { User, Image as ImageIcon, MessageSquare, Calendar, Upload, Eye, Send, Settings, LogOut, Plus, Edit3, Wifi, WifiOff, ExternalLink, Zap, Download, Save, Mail, UserCircle } from 'lucide-react';
import apiService from '../services/api';
import LoginForm from './LoginForm';
import { useCanvasGenerator, CanvasPreview } from './CanvasImageGenerator';
import EmojiSelector from './EmojiSelector';
import RichTextEditor from './RichTextEditor';

// Fonction utilitaire pour calculer la taille de police de manière cohérente avec le Canvas
// Utilise un ratio constant basé sur 1080px (taille finale) pour garantir la cohérence
const calculateFontSizeForPreview = (cssSize, previewWidth) => {
  // Ratio de référence : 1080px est la taille finale standard
  const referenceWidth = 1080;
  const scaleRatio = previewWidth / referenceWidth;
  
  // Convertir les tailles CSS en pixels pour la prévisualisation
  // Les valeurs sont basées sur 1080px et sont ensuite scalées
  const sizeMap = {
    'small': 32 * scaleRatio,      // ~32px à 1080px
    'medium': 54 * scaleRatio,     // ~54px à 1080px
    'large': 86 * scaleRatio,      // ~86px à 1080px
    'x-large': 130 * scaleRatio    // ~130px à 1080px
  };

  if (sizeMap[cssSize]) {
    return `${Math.round(sizeMap[cssSize])}px`;
  }

  // Si c'est déjà en pixels (e.g., "24px"), convertir en taille à 1080px puis scaler
  const pxMatch = String(cssSize).match(/(\d+)px/);
  if (pxMatch) {
    const baseSize = parseInt(pxMatch[1]);
    return `${Math.round(baseSize * scaleRatio)}px`;
  }

  return `${Math.round(54 * scaleRatio)}px`; // Défaut : medium
};

const ImagePreview = ({ template, data, onNavigate, currentCarouselIndex, setCurrentCarouselIndex }) => {
  // Cette fonction gère TOUS les aperçus.
  
  if (!template) return null;

  // 1. APERÇU POUR CARROUSEL APRÈS GÉNÉRATION
  if (template.format === 'carousel' && data.generatedMedia && data.generatedMedia.length > 0) {
    return (
      <div className="space-y-4">
        {/* Navigation */}
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-gray-500">
            Aperçu du Carrousel ({data.generatedMedia.length} médias)
            <span className="ml-2 text-green-600">✅ Généré</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentCarouselIndex(i => Math.max(0, i - 1))}
              disabled={currentCarouselIndex === 0}
              className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50"
            >←</button>
            <span className="text-sm font-medium">
              {currentCarouselIndex + 1} / {data.generatedMedia.length}
            </span>
            <button
              onClick={() => setCurrentCarouselIndex(i => Math.min(data.generatedMedia.length - 1, i + 1))}
              disabled={currentCarouselIndex === data.generatedMedia.length - 1}
              className="p-1 rounded-full hover:bg-gray-100 disabled:opacity-50"
            >→</button>
          </div>
        </div>
        {/* Média principal */}
        <div className="relative rounded-lg overflow-hidden aspect-square bg-gray-900">
          {data.generatedMedia[currentCarouselIndex].type === 'video' ? (
             <video src={data.generatedMedia[currentCarouselIndex].previewUrl} className="w-full h-full object-contain" controls />
          ) : (
             <img src={data.generatedMedia[currentCarouselIndex].previewUrl} alt={`Média ${currentCarouselIndex + 1}`} className="w-full h-full object-contain" />
          )}
          <div className="absolute bottom-2 right-2 text-white text-xs bg-black bg-opacity-50 px-2 py-1 rounded">
            @causecommune93.1
          </div>
        </div>
        {/* Miniatures */}
        <div className="grid grid-cols-5 gap-2 mt-4">
          {data.generatedMedia.map((media, index) => (
            <button key={index} onClick={() => setCurrentCarouselIndex(index)} className={`relative rounded-lg overflow-hidden aspect-square ${currentCarouselIndex === index ? 'ring-2 ring-blue-500' : ''}`}>
              {media.type === 'video' ? (
                <video src={media.previewUrl} className="w-full h-full object-cover" muted playsInline />
              ) : (
                <img src={media.previewUrl} alt={`Miniature ${index + 1}`} className="w-full h-full object-cover" />
              )}
              <div className="absolute top-1 right-1 bg-black bg-opacity-50 text-white text-xs px-1 rounded">
                {index + 1}
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // 2. APERÇU POUR POST SIMPLE OU STORY
  const getAspectRatio = (format) => {
    switch (format) {
      case 'story':
        return '9/16';
      case 'landscape':
        return '1.91/1';
      case 'portrait':
        return '4/5';
      case 'carousel':
        return '1/1';
      default:
        return '1/1'; // square
    }
  };
  const aspectRatio = getAspectRatio(template.format);
  
  // === FIX: Utiliser le fond personnalisé s'il existe ===
  const backgroundStyle = data.customBg
    ? { backgroundImage: `url(${data.customBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: template.style.background };

  return (
    <div
      className="w-full text-white font-sans overflow-hidden shadow-lg relative"
      style={{
        aspectRatio,
        ...backgroundStyle,
        color: template.style.textColor,
        maxWidth: template.format === 'story' ? '300px' : template.format === 'landscape' ? '600px' : '500px',
        margin: '0 auto'
      }}
    >
      {/* === FIX: Ajouter un overlay pour la lisibilité sur fond personnalisé === */}
      {data.customBg && <div className="absolute inset-0 w-full h-full bg-black bg-opacity-40"></div>}
      
      {/* Contenu textuel */}
      <div className="relative z-10 h-full p-6 flex flex-col justify-center items-center">
        {/* Utiliser template.fields si disponible, sinon fallback sur Object.entries(template.layout) */}
        {(template.fields && template.fields.length > 0 ? template.fields : Object.keys(template.layout || {})).map((key) => {
          const value = template.layout[key];
          if (!value) {
            return null;
          }
          
          // Calculer la largeur de prévisualisation pour le calcul de la taille de police
          const previewWidth = template.format === 'story' ? 300 : template.format === 'landscape' ? 600 : 500;
          
          // Utiliser la taille personnalisée si fournie, sinon calculer depuis le layout
          const customFontSize = data.customFontSize?.[key] || data.customFontSize || null;
          const fontSize = customFontSize 
            ? (typeof customFontSize === 'number' 
                ? `${Math.round(customFontSize * (previewWidth / 1080))}px`
                : customFontSize)
            : calculateFontSizeForPreview(value.size, previewWidth);
          
          // Utiliser la police personnalisée si fournie, sinon celle du template, sinon Arial
          const fontFamily = data.customFontFamily?.[key] || data.customFontFamily || template.style.fontFamily || 'Arial, sans-serif';
          
          // Utiliser la couleur personnalisée si fournie, sinon la couleur du template
          const textColor = data.customTextColor || template.style.textColor || '#000000';
          
          // Obtenir le texte depuis les données
          const text = data[key] || `[${key.toUpperCase()}]`;
          
          return (
            <div
              key={key}
              className="absolute"
              style={{
                left: `${value.x}%`,
                top: `${value.y}%`,
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                width: '90%',
                fontSize: fontSize,
                fontWeight: value.weight,
                fontStyle: value.style || 'normal',
                fontFamily: fontFamily,
                color: textColor,
                textShadow: '1px 1px 3px rgba(0,0,0,0.5)',
                zIndex: 20,
              }}
            >
              {text}
            </div>
          );
        })}
      </div>
      
      {/* Watermark */}
      <div className="absolute bottom-4 right-4 text-sm font-medium z-20" style={{ color: template.style.accentColor }}>
        @causecommune93.1
      </div>
    </div>
  );
};

const InstagramTeamApp = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState('create');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [postData, setPostData] = useState({
    image: null,
    caption: '',
    altText: '', // Texte alternatif pour l'accessibilité
    template: null,
    postType: 'post',
    videoFile: null,
    carouselImages: [],
    imageFiles: [],
    carouselMedia: [],
    generatedMedia: null // Remplace `generatedMediaFiles`
  });
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishMessage, setPublishMessage] = useState('');
  const [apiStatus, setApiStatus] = useState('checking');
  const [postsHistory, setPostsHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [showWelcomeNotification, setShowWelcomeNotification] = useState(false);
  const [hasChangedDefaultPassword, setHasChangedDefaultPassword] = useState(false);
  const [useAutoGeneration, setUseAutoGeneration] = useState(true); // Mode génération automatique par défaut
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [generatedImagePreview, setGeneratedImagePreview] = useState(null);
  const [generatedVideoPreview, setGeneratedVideoPreview] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState(null); // Image de fond
  const [backgroundImageFile, setBackgroundImageFile] = useState(null);
  const [originalUploadedImage, setOriginalUploadedImage] = useState(null); // Image originale uploadée (sans texte)
  const [originalUploadedImageFile, setOriginalUploadedImageFile] = useState(null); // Fichier original uploadé
  const [customTextColor, setCustomTextColor] = useState('#FFFFFF'); // Couleur du texte personnalisée
  const [customFontFamily, setCustomFontFamily] = useState({}); // Police personnalisée par champ {field: 'font'}
  const [customFontSize, setCustomFontSize] = useState({}); // Taille de police personnalisée par champ {field: size}
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  // Système de nettoyage des blob URLs
  const [activeBlobURLs, setActiveBlobURLs] = useState([]);
  
  // États pour la gestion du profil
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    role: '',
    avatar: ''
  });
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [profileErrors, setProfileErrors] = useState({});

  // Refs
  const fileInputRef = useRef(null);
  const backgroundImageInputRef = useRef(null);
  const previewSectionRef = useRef(null); // Référence pour la section de prévisualisation

  // Hook Canvas Generator
  const { generateImage } = useCanvasGenerator();

  // Tous les useEffect groupés ensemble
  useEffect(() => {
    checkApiConnection();
    checkExistingAuth();
  }, []);

  useEffect(() => {
    if (currentUser) {
      loadTemplates();
    }
  }, [currentUser]);

  useEffect(() => {
    setCurrentCarouselIndex(0);
  }, [postData.generatedMedia]);

  useEffect(() => {
    if (activeTab === 'history' && currentUser) {
      loadPostsHistory();
    }
  }, [activeTab, currentUser]);

  // Nettoyer les blob URLs au démontage du composant
  useEffect(() => {
    return () => {
      cleanupBlobURLs();
    };
  }, []);

  // Initialiser les données du profil quand l'utilisateur change
  useEffect(() => {
    if (currentUser) {
      setProfileData({
        name: currentUser.name || '',
        email: currentUser.email || '',
        role: currentUser.role || '',
        avatar: currentUser.avatar || '👤'
      });
    }
  }, [currentUser]);

  // Fonction pour nettoyer les blob URLs
  const cleanupBlobURLs = () => {
    activeBlobURLs.forEach(url => {
      try {
        URL.revokeObjectURL(url);
      } catch (error) {
        console.warn('Failed to revoke blob URL:', error);
      }
    });
    setActiveBlobURLs([]);
  };

  // Fonction de réinitialisation complète
  const resetCarouselGeneration = () => {
    // Nettoyer les blob URLs
    cleanupBlobURLs();
    
    // Réinitialiser complètement l'état
    setPostData(data => ({
      ...data,
      generatedMedia: null
    }));
    
    // Réinitialiser les messages
    setPublishMessage('');
    setCurrentCarouselIndex(0);
    
    console.log('[RESET] Carrousel entièrement réinitialisé');
  };

  const checkExistingAuth = async () => {
    const token = apiService.getToken();
    if (token) {
      try {
        const response = await apiService.getProfile();
        if (response.success) {
          setCurrentUser(response.user);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        apiService.setToken(null);
      }
    }
  };

  const checkApiConnection = async () => {
    try {
      await apiService.healthCheck();
      setApiStatus('connected');
    } catch (error) {
      console.error('API connection failed:', error);
      setApiStatus('disconnected');
    }
  };

  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const templatesData = await apiService.getTemplates({ active: true });
      
      // Convertir les templates de la base de données vers le format attendu par le frontend
      // Conversion : style, layout (déjà JSONB), carousel_config -> carouselConfig, video_config -> videoConfig
      const convertedTemplates = templatesData.map(template => ({
        ...template,
        style: typeof template.style === 'string' ? JSON.parse(template.style) : template.style,
        layout: typeof template.layout === 'string' ? JSON.parse(template.layout) : template.layout,
        carouselConfig: template.carousel_config 
          ? (typeof template.carousel_config === 'string' 
              ? JSON.parse(template.carousel_config) 
              : template.carousel_config)
          : undefined,
        videoConfig: template.video_config 
          ? (typeof template.video_config === 'string' 
              ? JSON.parse(template.video_config) 
              : template.video_config)
          : undefined,
      }));
      
      setTemplates(convertedTemplates);
    } catch (error) {
      console.error('Failed to load templates:', error);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadPostsHistory = async () => {
    setLoadingHistory(true);
    try {
      const history = await apiService.getPostsHistory();
      setPostsHistory(history);
    } catch (error) {
      console.error('Failed to load history:', error);
      setPostsHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Définir les handlers avant le return conditionnel
  const handleLogin = async (email, password) => {
    setIsLoggingIn(true);
    setLoginError('');
    
    try {
      const response = await apiService.login(email, password);
      if (response.success) {
        setCurrentUser(response.user);
        // Afficher la notification de bienvenue si c'est un mot de passe par défaut
        if (password === 'changeme123!') {
          setShowWelcomeNotification(true);
        }
      }
    } catch (error) {
      console.error('Login failed:', error);
      setLoginError(error.message || 'Erreur de connexion');
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Afficher le formulaire de connexion si l'utilisateur n'est pas connecté
  if (!currentUser) {
    return (
      <LoginForm 
        onLogin={handleLogin}
        isLoading={isLoggingIn}
        error={loginError}
      />
    );
  }

  const handleLogout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setCurrentUser(null);
      setActiveTab('create');
      setSelectedTemplate(null);
      setPostData({ image: null, caption: '', altText: '', template: null, postType: 'post', videoFile: null, carouselImages: [], imageFiles: [], carouselMedia: [], generatedMedia: null });
      setPublishMessage('');
      setPostsHistory([]);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);

    // Définir automatiquement le type de post en fonction du format du template
    let postType = 'post'; // Par défaut pour 'square'
    if (template.format === 'story') {
      // Différencier Story et Reel
      if (template.category === 'video') {
        postType = 'reel';
      } else {
        postType = 'story';
      }
    } else if (template.format === 'carousel') {
      postType = 'carousel';
    } else if (template.format === 'landscape') {
      postType = 'landscape'; // Les posts paysage utilisent le type 'landscape'
    } else if (template.format === 'portrait') {
      postType = 'portrait'; // Les posts portrait utilisent le type 'portrait'
    }

    setPostData({
      ...postData,
      template: template,
      postType: postType, // Le type est maintenant défini par le template
      // Réinitialiser les données non pertinentes
      generatedMedia: null,
      carouselImages: [],
      imageFiles: [],
      videoFile: null,
      imageFile: null
    });
    // Réinitialiser l'index du carrousel au cas où
    setCurrentCarouselIndex(0);
  };

  // Fonction pour redimensionner/recadrer une image selon le format du template
  const resizeImageToFormat = (imageSrc, format, originalFileName) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        // Dimensions cibles selon le format
        const getTargetDimensions = (format) => {
          const baseWidth = 1080;
          switch (format) {
            case 'story':
              return { width: baseWidth, height: 1920 }; // 9:16
            case 'landscape':
              return { width: baseWidth, height: 566 }; // 1.91:1
            case 'portrait':
              return { width: baseWidth, height: 1350 }; // 4:5
            case 'square':
            default:
              return { width: baseWidth, height: baseWidth }; // 1:1
          }
        };

        const target = getTargetDimensions(format);
        const targetAspectRatio = target.width / target.height;
        const sourceAspectRatio = img.width / img.height;

        // Créer un canvas pour le redimensionnement
        const canvas = document.createElement('canvas');
        canvas.width = target.width;
        canvas.height = target.height;
        const ctx = canvas.getContext('2d');

        // Calculer les dimensions et position pour le recadrage centré
        let sourceX = 0;
        let sourceY = 0;
        let sourceWidth = img.width;
        let sourceHeight = img.height;
        let destX = 0;
        let destY = 0;
        let destWidth = target.width;
        let destHeight = target.height;

        if (sourceAspectRatio > targetAspectRatio) {
          // L'image source est plus large que la cible
          // On recadre les côtés (crop horizontal)
          sourceHeight = img.height;
          sourceWidth = img.height * targetAspectRatio;
          sourceX = (img.width - sourceWidth) / 2;
        } else {
          // L'image source est plus haute que la cible
          // On recadre le haut/bas (crop vertical)
          sourceWidth = img.width;
          sourceHeight = img.width / targetAspectRatio;
          sourceY = (img.height - sourceHeight) / 2;
        }

        // Dessiner l'image recadrée et redimensionnée
        ctx.drawImage(
          img,
          sourceX, sourceY, sourceWidth, sourceHeight, // Source (recadrage)
          destX, destY, destWidth, destHeight // Destination (redimensionnement)
        );

        // Convertir en blob puis en File
        canvas.toBlob((blob) => {
          if (blob) {
            const fileName = originalFileName || 'formatted-image.jpg';
            const resizedFile = new File([blob], fileName, { type: 'image/jpeg' });
            const resizedDataUrl = canvas.toDataURL('image/jpeg', 0.95);
            resolve({ file: resizedFile, dataUrl: resizedDataUrl });
          } else {
            reject(new Error('Erreur lors du redimensionnement'));
          }
        }, 'image/jpeg', 0.95);
      };

      img.onerror = reject;
      img.src = imageSrc;
    });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Afficher un message de traitement
      setPublishMessage('⏳ Traitement de l\'image...');
      
      const reader = new FileReader();
      reader.onload = async (e) => {
        const originalImageSrc = e.target.result;
        
        // Sauvegarder l'image originale (sans texte) pour les générations futures
        setOriginalUploadedImage(originalImageSrc);
        setOriginalUploadedImageFile(file);
        
        // Si un template est sélectionné, redimensionner selon son format
        if (selectedTemplate && selectedTemplate.format) {
          try {
            const { file: resizedFile, dataUrl: resizedDataUrl } = await resizeImageToFormat(
              originalImageSrc,
              selectedTemplate.format,
              file.name
            );
            
            setPostData({ 
              ...postData, 
              image: resizedDataUrl, 
              imageFile: resizedFile 
            });
            setPublishMessage('✅ Image formatée selon le template');
          } catch (error) {
            console.error('Erreur lors du redimensionnement:', error);
            // En cas d'erreur, utiliser l'image originale
            setPostData({ ...postData, image: originalImageSrc, imageFile: file });
            setPublishMessage('⚠️ Image originale utilisée (erreur de formatage)');
          }
        } else {
          // Pas de template sélectionné, utiliser l'image telle quelle
          setPostData({ ...postData, image: originalImageSrc, imageFile: file });
          setPublishMessage('');
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBackgroundImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setBackgroundImage(e.target.result);
        setBackgroundImageFile(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeBackgroundImage = () => {
    setBackgroundImage(null);
    setBackgroundImageFile(null);
    if (backgroundImageInputRef.current) {
      backgroundImageInputRef.current.value = '';
    }
  };

  const handleVideoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validation de taille (100MB max)
      if (file.size > 100 * 1024 * 1024) {
        alert('La vidéo ne peut pas dépasser 100MB');
        return;
      }

      // Validation de format
      const allowedTypes = ['video/mp4', 'video/mov', 'video/avi'];
      if (!allowedTypes.includes(file.type)) {
        alert('Format vidéo non supporté. Utilisez MP4, MOV ou AVI.');
        return;
      }

      const videoUrl = URL.createObjectURL(file);
      setPostData({
        ...postData,
        videoFile: file,
        videoPreview: videoUrl
      });
    }
  };

  const handleCarouselImagesUpload = (event) => {
    const files = Array.from(event.target.files);
    
    // Validation du nombre de médias
    const min = selectedTemplate?.carouselConfig?.minImages || 2;
    const max = selectedTemplate?.carouselConfig?.maxImages || 10;
    if (files.length < min || files.length > max) {
      alert(`Veuillez sélectionner entre ${min} et ${max} médias.`);
      return;
    }

    // Validation du type de chaque fichier
    const allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif'];
    const allowedVideoTypes = ['video/mp4', 'video/mov'];
    const allAllowedTypes = [...allowedImageTypes, ...allowedVideoTypes];

    const invalidFiles = files.filter(file => !allAllowedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      alert(`Certains fichiers ont un format non supporté. Formats acceptés : JPEG, PNG, GIF, MP4, MOV.`);
      return;
    }

    // Créer les objets de prévisualisation
    const mediaPreviewObjects = files.map(file => ({
      file: file,
      type: file.type.startsWith('video/') ? 'video' : 'image',
      previewUrl: URL.createObjectURL(file)
    }));
    
    setPostData({
      ...postData,
      imageFiles: files, // On garde ce nom pour l'instant, mais il contient des vidéos aussi
      carouselImages: mediaPreviewObjects.map(m => m.previewUrl), // On garde aussi ce nom
      carouselMedia: mediaPreviewObjects // Le nouvel état pour avoir plus d'infos
    });
  };

  const removeCarouselImage = (index) => {
    const newFiles = postData.imageFiles.filter((_, i) => i !== index);
    const newPreviews = postData.carouselImages.filter((_, i) => i !== index);
    const newMedia = postData.carouselMedia.filter((_, i) => i !== index);
    
    setPostData({
      ...postData,
      imageFiles: newFiles,
      carouselImages: newPreviews,
      carouselMedia: newMedia
    });
  };

  // 1. Améliorer handleAutoGenerateImage pour qu'elle retourne le fichier
  const handleAutoGenerateImage = async () => {
    if (!selectedTemplate) return null;

    setIsGeneratingImage(true);
    setPublishMessage('⏳ Génération de l\'image...');
    try {
      const templateData = { ...postData };
      
      // Priorité : image originale uploadée > image de fond personnalisée > fond du template
      if (!postData.autoGenerate) {
        // En mode manuel, utiliser l'image originale uploadée (sans texte) comme fond
        // Cela évite d'utiliser une image générée précédemment qui contiendrait déjà du texte
        const imageToUse = originalUploadedImage || postData.image;
        if (imageToUse) {
          templateData.backgroundImage = imageToUse;
          // Utiliser la couleur de texte personnalisée si en mode upload manuel
          templateData.customTextColor = customTextColor;
        }
      } else if (backgroundImage) {
        // Utiliser l'image de fond personnalisée (pour les templates square)
        templateData.backgroundImage = backgroundImage;
      }
      
      // Ajouter les paramètres personnalisés de police et taille (par champ)
      if (Object.keys(customFontFamily).length > 0) {
        templateData.customFontFamily = customFontFamily;
      }
      if (Object.keys(customFontSize).length > 0) {
        templateData.customFontSize = customFontSize;
      }
      if (customTextColor) {
        templateData.customTextColor = customTextColor;
      }
      
      const generatedBlob = await generateImage(selectedTemplate, templateData);
      const imageUrl = URL.createObjectURL(generatedBlob);
      setGeneratedImagePreview(imageUrl);

      const generatedFile = new File([generatedBlob], "generated-post.png", { type: "image/png" });
      // IMPORTANT: Ne pas écraser postData.image avec l'image générée
      // Garder l'image originale pour les générations futures
      setPostData(data => ({ ...data, imageFile: generatedFile }));
      // Ne pas modifier postData.image pour garder l'image originale
      
      setPublishMessage('✅ Image générée. Prête à être publiée.');
      
      // Scroll automatique vers la prévisualisation après génération
      setTimeout(() => {
        previewSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      
      return generatedFile; // Retourner le fichier pour la publication
    } catch (error) {
      console.error("Image generation failed:", error);
      setPublishMessage(`❌ Erreur de génération: ${error.message}`);
      return null; // Retourner null en cas d'erreur
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // Fonction pour générer une vidéo avec overlay
  const handleAutoGenerateVideo = async () => {
    if (!selectedTemplate || !postData.videoFile) return null;

    setIsGeneratingImage(true);
    setPublishMessage('⏳ Génération de la vidéo avec overlay...');
    try {
      const formData = new FormData();
      formData.append('video', postData.videoFile);
      formData.append('templateId', selectedTemplate.id);
      
      // Préparer les données du template
      const templateData = { ...postData };
      
      // Ajouter les paramètres personnalisés
      if (Object.keys(customFontFamily).length > 0) {
        templateData.customFontFamily = customFontFamily;
      }
      if (Object.keys(customFontSize).length > 0) {
        templateData.customFontSize = customFontSize;
      }
      if (customTextColor) {
        templateData.customTextColor = customTextColor;
      }
      
      formData.append('data', JSON.stringify(templateData));
      
      // Options de génération
      const options = {
        overlayOpacity: 0.15,
        quality: 'medium'
      };
      formData.append('options', JSON.stringify(options));

      const result = await apiService.generateVideoOverlay(formData);
      
      // Télécharger la vidéo générée depuis l'URL
      const response = await fetch(result.videoUrl);
      const blob = await response.blob();
      const generatedFile = new File([blob], `generated-video-${Date.now()}.mp4`, { type: "video/mp4" });
      
      // Créer une URL de prévisualisation
      const videoPreviewUrl = URL.createObjectURL(blob);
      setGeneratedVideoPreview(videoPreviewUrl);
      
      // Mettre à jour l'état avec la vidéo générée
      setPostData(data => ({ ...data, videoFile: generatedFile }));
      setPublishMessage('✅ Vidéo générée. Prête à être publiée.');
      
      // Scroll automatique vers la prévisualisation après génération
      setTimeout(() => {
        previewSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      
      return generatedFile;
    } catch (error) {
      console.error("Video generation failed:", error);
      setPublishMessage(`❌ Erreur de génération vidéo: ${error.message}`);
      return null;
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // Fonction pour télécharger l'image générée (post simple/story)
  const handleDownloadGenerated = () => {
    if (generatedImagePreview) {
      const link = document.createElement('a');
      link.download = `generated-image-${Date.now()}.jpg`;
      link.href = generatedImagePreview;
      link.click();
    } else if (postData.imageFile) {
      // Si l'image n'a pas été générée mais existe déjà
      const url = URL.createObjectURL(postData.imageFile);
      const link = document.createElement('a');
      link.download = `generated-image-${Date.now()}.jpg`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  // Fonction pour télécharger les images de carrousel générées
  const handleDownloadCarouselImages = () => {
    if (postData.generatedMedia && postData.generatedMedia.length > 0) {
      postData.generatedMedia.forEach((media, index) => {
        setTimeout(() => {
          const link = document.createElement('a');
          link.download = `carousel-image-${index + 1}-${Date.now()}.jpg`;
          link.href = media.previewUrl;
          link.click();
        }, index * 200); // Délai de 200ms entre chaque téléchargement
      });
    }
  };

  // Ajout de la fonction pour changer le mot de passe
  const handleChangePassword = async () => {
    // Validation côté client
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordMessage('Tous les champs sont requis');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage('Les nouveaux mots de passe ne correspondent pas');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordMessage('Le nouveau mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setIsChangingPassword(true);
    setPasswordMessage('');

    try {
      const response = await apiService.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );
      
      if (response.success) {
        setPasswordMessage('Mot de passe changé avec succès !');
        setHasChangedDefaultPassword(true);
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      console.error('Change password failed:', error);
      setPasswordMessage(error.message || 'Erreur lors du changement de mot de passe');
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Validation du profil
  const validateProfile = (data) => {
    const errors = {};
    
    if (!data.name || data.name.trim().length === 0) {
      errors.name = 'Le nom est requis';
    }
    
    if (!data.email || data.email.trim().length === 0) {
      errors.email = 'L\'email est requis';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.email)) {
        errors.email = 'Format d\'email invalide';
      }
    }
    
    if (!data.role || data.role.trim().length === 0) {
      errors.role = 'Le rôle/émission est requis';
    }
    
    return errors;
  };

  // Mise à jour du profil
  const handleUpdateProfile = async () => {
    const errors = validateProfile(profileData);
    setProfileErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      setProfileMessage('Veuillez corriger les erreurs ci-dessous');
      return;
    }
    
    setIsUpdatingProfile(true);
    setProfileMessage('');
    
    try {
      const response = await apiService.updateProfile(profileData);
      
      if (response.success) {
        setCurrentUser(response.user);
        setProfileMessage('Profil mis à jour avec succès !');
      }
    } catch (error) {
      console.error('Update profile failed:', error);
      if (error.message.includes('Email déjà utilisé')) {
        setProfileErrors({ email: 'Cette adresse email est déjà utilisée' });
        setProfileMessage('Email déjà utilisé par un autre compte');
      } else {
        setProfileMessage(error.message || 'Erreur lors de la mise à jour du profil');
      }
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  // Réinitialiser le formulaire de profil
  const handleResetProfile = () => {
    if (currentUser) {
      setProfileData({
        name: currentUser.name || '',
        email: currentUser.email || '',
        role: currentUser.role || '',
        avatar: currentUser.avatar || '👤'
      });
      setProfileErrors({});
      setProfileMessage('');
    }
  };

  const generateCarouselImages = async (template, data, images) => {
    console.log('[GENERATION] Début génération carrousel:', {
      templateId: template.id,
      templateName: template.name,
      imagesCount: images.length,
      data: data
    });

    const generatedImages = [];
    
    try {
      // Cas spécial pour le template "Avant / Après"
      if (template.id === 9) {
        console.log("[GENERATION] Template Avant/Après détecté");
        const [imageAvant, imageApres] = images;
        const blobs = await Promise.all([
          createCleanImageSlide(imageAvant, template, {
            label: "AVANT",
            title: data.title || '',
            subtitle: data.before_text || ''
          }),
          createCleanImageSlide(imageApres, template, {
            label: "APRÈS",
            title: data.title || '',
            subtitle: data.after_text || ''
          })
        ]);
        return blobs;
      }

      // Templates de carrousel normaux (incluant Galerie Photo)
      for (let i = 0; i < images.length; i++) {
        console.log(`[GENERATION] Génération image ${i + 1}/${images.length} pour template ${template.name}`);
        
        // Préparer les données selon le template
        let slideData = {};
        
        if (template.id === 5) { // Galerie Photo
          slideData = {
            title: data.title || '',
            description: data.description || '',
            imageNumber: `${i + 1}/${images.length}`
          };
        } else {
          // Autres templates de carrousel
          slideData = {
            title: i === 0 ? (data.title || '') : '', // Titre seulement sur la première image
            subtitle: i === 0 ? (data.subtitle || '') : '', // Sous-titre seulement sur la première image
            imageNumber: `${i + 1}/${images.length}`
          };
        }
        
        console.log(`[GENERATION] Données pour slide ${i + 1}:`, slideData);
        
        // Utiliser createCleanImageSlide pour tous les templates
        const blob = await createCleanImageSlide(images[i], template, slideData);
        generatedImages.push(blob);
      }

      console.log('[GENERATION] Génération terminée avec succès');
      return generatedImages;
      
    } catch (error) {
      console.error('[GENERATION] Erreur:', error);
      throw error;
    }
  };

  const createCleanImageSlide = async (imageSrc, template, textData) => {
    console.log('[CLEAN_SLIDE] === DÉBUT CRÉATION SLIDE ===');
    console.log('[CLEAN_SLIDE] Template:', template.name);
    console.log('[CLEAN_SLIDE] TextData reçu:', textData);
    console.log('[CLEAN_SLIDE] ImageSrc:', imageSrc);
    
    // Créer un canvas complètement neuf et isolé
    const canvas = document.createElement('canvas');
    canvas.width = 1080;
    canvas.height = 1080;
    const ctx = canvas.getContext('2d');
    
    try {
      // IMPORTANT: Nettoyer complètement le canvas
      ctx.clearRect(0, 0, 1080, 1080);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1.0;

      // 1. Fond template UNIQUEMENT
      if (template.style && template.style.background) {
        if (template.style.background.includes('gradient')) {
          const colors = template.style.background.match(/#[a-fA-F0-9]{6}/gi);
          if (colors && colors.length >= 2) {
            const gradient = ctx.createLinearGradient(0, 0, 1080, 1080);
            gradient.addColorStop(0, colors[0]);
            gradient.addColorStop(1, colors[1]);
            ctx.fillStyle = gradient;
          } else {
            ctx.fillStyle = '#2c3e50';
          }
        } else {
          ctx.fillStyle = template.style.background;
        }
      } else {
        ctx.fillStyle = '#2c3e50';
      }
      ctx.fillRect(0, 0, 1080, 1080);

      // 2. Image utilisateur si fournie
      if (imageSrc) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = imageSrc;
        });
        ctx.drawImage(img, 0, 0, 1080, 1080);
      }

      // 3. Overlay
      ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
      ctx.fillRect(0, 0, 1080, 1080);
      
      // 4. Textes UNIQUEMENT selon textData
      ctx.fillStyle = template.style?.textColor || '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // Pour le template Avant/Après (id: 9)
      if (template.id === 9) {
        // Titre principal seulement sur la première slide (AVANT)
        if (textData.label === "AVANT" && textData.title && textData.title.trim()) {
          console.log('[CLEAN_SLIDE] Affichage titre AVANT:', textData.title.trim());
          ctx.font = 'bold 64px Arial';
          ctx.fillText(textData.title.trim(), 540, 150);
        }
        
        // Label AVANT/APRÈS (position centrale)
        if (textData.label) {
          console.log('[CLEAN_SLIDE] Affichage label:', textData.label);
          ctx.font = 'bold 80px Arial';
          ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
          ctx.lineWidth = 3;
          ctx.strokeText(textData.label, 540, 540);
          ctx.fillText(textData.label, 540, 540);
        }

        // Sous-titre (en bas)
        if (textData.subtitle && textData.subtitle.trim()) {
          console.log('[CLEAN_SLIDE] Affichage sous-titre:', textData.subtitle.trim());
          ctx.font = 'normal 42px Arial';
          ctx.fillStyle = template.style?.textColor || '#ffffff';
          ctx.fillText(textData.subtitle.trim(), 540, 920);
        }
      } 
      // Pour le template Galerie Photo (id: 5)
      else if (template.id === 5) {
        console.log('[CLEAN_SLIDE] Template Galerie Photo détecté');
        
        // Numéro de l'image (en haut à droite)
        if (textData.imageNumber) {
          console.log('[CLEAN_SLIDE] Affichage numéro:', textData.imageNumber);
          ctx.font = 'bold 32px Arial';
          ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
          ctx.textAlign = 'right';
          ctx.fillText(textData.imageNumber, 1020, 80);
          ctx.textAlign = 'center'; // Remettre au centre pour les autres textes
        }
        
        // Titre principal (en haut) - seulement sur la première image
        if (textData.title && textData.title.trim() && textData.imageNumber === '1/2') {
          console.log('[CLEAN_SLIDE] Affichage titre galerie:', textData.title.trim());
          ctx.font = 'bold 56px Arial';
          ctx.fillStyle = template.style?.textColor || '#ffffff';
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.7)';
          ctx.lineWidth = 2;
          ctx.strokeText(textData.title.trim(), 540, 200);
          ctx.fillText(textData.title.trim(), 540, 200);
        }

        // Description (en bas) - seulement sur la première image
        if (textData.description && textData.description.trim() && textData.imageNumber === '1/2') {
          console.log('[CLEAN_SLIDE] Affichage description galerie:', textData.description.trim());
          ctx.font = 'normal 36px Arial';
          ctx.fillStyle = template.style?.textColor || '#ffffff';
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
          ctx.lineWidth = 1;
          ctx.strokeText(textData.description.trim(), 540, 900);
          ctx.fillText(textData.description.trim(), 540, 900);
        }
      } 
      else {
        // Logique pour les autres templates
        
        // Numéro de l'image
        if (textData.imageNumber) {
          ctx.font = 'bold 32px Arial';
          ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
          ctx.textAlign = 'right';
          ctx.fillText(textData.imageNumber, 1020, 80);
          ctx.textAlign = 'center';
        }
        
        if (textData.title && textData.title.trim()) {
          ctx.font = 'bold 72px Arial';
          ctx.fillText(textData.title.trim(), 540, 180);
        }
        
        if (textData.label) {
          ctx.font = 'bold 48px Arial';
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
          ctx.fillText(textData.label, 540, 540);
        }

        if (textData.subtitle && textData.subtitle.trim()) {
          ctx.font = 'normal 36px Arial';
          ctx.fillStyle = template.style?.textColor || '#ffffff';
          ctx.fillText(textData.subtitle.trim(), 540, 900);
        }
      }
      
      // Watermark
      ctx.font = '24px Arial';
      ctx.fillStyle = template.style?.accentColor || '#ffffff';
      ctx.textAlign = 'right';
      ctx.fillText('@causecommune93.1', 1050, 1050);

      console.log('[CLEAN_SLIDE] === SLIDE TERMINÉE ===');

      // Conversion en blob
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.95));
      
      // Nettoyer le canvas
      canvas.remove();
      
      return blob;
      
    } catch (error) {
      console.error('[CLEAN_SLIDE] Erreur:', error);
      canvas.remove();
      throw error;
    }
  };

  const handleGenerateImages = async () => {
    if (!selectedTemplate) {
      setPublishMessage('❌ Veuillez sélectionner un template');
      return;
    }

    if (postData.postType === 'carousel') {
      if (!postData.carouselImages || postData.carouselImages.length < 2) {
        setPublishMessage('❌ Le carrousel nécessite au moins 2 images');
        return;
      }

      try {
        setPublishMessage('⏳ Génération des médias en cours...');
        
        // IMPORTANT: Nettoyer d'abord toutes les anciennes blob URLs
        cleanupBlobURLs();
        
        // Réinitialiser complètement l'état généré
        setPostData(data => ({ ...data, generatedMedia: null }));
        
        // Petit délai pour s'assurer que l'état est bien nettoyé
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Utiliser la logique appropriée selon le template
        console.log('[GENERATE] Génération avec template:', selectedTemplate.name);
        const generatedBlobs = await generateCarouselImages(selectedTemplate, postData, postData.carouselImages);
        
        // Créer les objets média avec nouvelles blob URLs
        const newBlobURLs = [];
        const generatedMedia = generatedBlobs.map((blob, index) => {
          const previewUrl = URL.createObjectURL(blob);
          newBlobURLs.push(previewUrl);
          return {
            file: blob,
            previewUrl: previewUrl,
            type: 'image'
          };
        });
        
        // Enregistrer les nouvelles blob URLs pour nettoyage futur
        setActiveBlobURLs(newBlobURLs);
        
        // Mettre à jour l'état avec les nouvelles images
        setPostData(data => ({ ...data, generatedMedia }));
        setPublishMessage('✅ Médias prêts pour la publication.');
        
        // Scroll automatique vers la prévisualisation après génération
        setTimeout(() => {
          previewSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
        
        console.log('[GENERATE] Génération terminée avec succès');
        
      } catch (error) {
        console.error('Erreur génération carrousel:', error);
        setPublishMessage(`❌ Erreur de génération: ${error.message}`);
      }
    } else {
      // Logique existante pour les posts/stories
      await handleAutoGenerateImage();
    }
  };

  const getPublishButtonState = () => {
    const { postType, caption, imageFile, videoFile, imageFiles, autoGenerate, generatedMedia } = postData;

    if (isPublishing) {
      return { isDisabled: true, message: "Publication en cours..." };
    }
    
    // Convertir HTML en texte brut pour validation
    const plainTextCaption = htmlToPlainText(caption || '');
    const trimmedCaption = plainTextCaption.trim();
    
    if (!trimmedCaption) {
      return { isDisabled: true, message: "Une légende est requise pour publier." };
    }

    // Validation de la longueur Instagram
    if (trimmedCaption.length > 2200) {
      return { isDisabled: true, message: `La caption dépasse la limite de 2200 caractères (${trimmedCaption.length} caractères).` };
    }

    // Validation des hashtags
    const hashtagValidation = validateHashtags(trimmedCaption);
    if (!hashtagValidation.valid) {
      return { isDisabled: true, message: hashtagValidation.error };
    }

    switch (postType) {
      case 'reel':
        return videoFile 
          ? { isDisabled: false, message: "Prêt à publier votre Reel." }
          : { isDisabled: true, message: "Veuillez ajouter un fichier vidéo." };

      case 'carousel':
        if (autoGenerate) {
          return (generatedMedia && generatedMedia.length > 0)
            ? { isDisabled: false, message: "Prêt à publier le carrousel généré." }
            : { isDisabled: true, message: "Générez d'abord les images du carrousel." };
        }
        return (imageFiles && imageFiles.length >= 2)
          ? { isDisabled: false, message: "Prêt à publier le carrousel." }
          : { isDisabled: true, message: "Veuillez ajouter au moins 2 images." };

      case 'post':
      case 'story':
      case 'landscape':
      case 'portrait':
        if (autoGenerate) {
           return imageFile
             ? { isDisabled: false, message: "Prêt à publier l'image générée." }
             : { isDisabled: true, message: "Générez d'abord votre image." };
        }
        return imageFile
          ? { isDisabled: false, message: "Prêt à publier." }
          : { isDisabled: true, message: "Veuillez ajouter une image." };
      
      default:
        return { isDisabled: true, message: "Sélectionnez un type de post valide." };
    }
  };

  // Fonction utilitaire pour convertir HTML en texte brut
  // Préserve les sauts de ligne et la structure pour Instagram
  const htmlToPlainText = (html) => {
    if (!html) return '';
    
    // Créer un élément temporaire pour extraire le texte
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Remplacer les balises de paragraphe par des sauts de ligne
    const paragraphs = tempDiv.querySelectorAll('p');
    paragraphs.forEach(p => {
      const br = document.createElement('br');
      p.parentNode.insertBefore(br, p.nextSibling);
    });
    
    // Remplacer les listes par du texte formaté
    const lists = tempDiv.querySelectorAll('ul, ol');
    lists.forEach(list => {
      const items = list.querySelectorAll('li');
      const listText = Array.from(items).map((li, index) => {
        const prefix = list.tagName === 'OL' ? `${index + 1}. ` : '• ';
        return prefix + (li.textContent || '').trim();
      }).join('\n');
      
      const textNode = document.createTextNode('\n' + listText + '\n');
      list.parentNode.replaceChild(textNode, list);
    });
    
    // Remplacer les <br> par des sauts de ligne
    const breaks = tempDiv.querySelectorAll('br');
    breaks.forEach(br => {
      br.parentNode.replaceChild(document.createTextNode('\n'), br);
    });
    
    // Extraire le texte final
    let text = tempDiv.textContent || tempDiv.innerText || '';
    
    // Nettoyer les sauts de ligne multiples (max 2 consécutifs)
    text = text.replace(/\n{3,}/g, '\n\n');
    
    // Nettoyer les espaces en début/fin de ligne
    text = text.split('\n').map(line => line.trim()).join('\n');
    
    return text.trim();
  };

  // Fonction pour valider les hashtags Instagram
  const validateHashtags = (text) => {
    const hashtags = text.match(/#\w+/g) || [];
    if (hashtags.length > 30) {
      return { valid: false, error: 'Maximum 30 hashtags autorisés sur Instagram' };
    }
    // Vérifier la longueur des hashtags (max 100 caractères par hashtag)
    for (const tag of hashtags) {
      if (tag.length > 100) {
        return { valid: false, error: `Le hashtag "${tag.substring(0, 20)}..." est trop long (max 100 caractères)` };
      }
    }
    return { valid: true };
  };

  // 3. Simplifier handlePublish
  const handlePublish = async () => {
    setIsPublishing(true);
    setPublishMessage('⏳ Publication en cours...');
    
    // Convertir le HTML en texte brut pour Instagram
    const plainTextCaption = htmlToPlainText(postData.caption || '');
    
    // Validation de la longueur
    if (plainTextCaption.length > 2200) {
      setPublishMessage('❌ La caption dépasse la limite de 2200 caractères Instagram');
      setIsPublishing(false);
      return;
    }

    // Validation des hashtags
    const hashtagValidation = validateHashtags(plainTextCaption);
    if (!hashtagValidation.valid) {
      setPublishMessage(`❌ ${hashtagValidation.error}`);
      setIsPublishing(false);
      return;
    }
    
    const formData = new FormData();
    formData.append('caption', plainTextCaption);
    formData.append('postType', postData.postType || 'post');
    formData.append('templateId', selectedTemplate?.id || null);
    
    // Ajouter le texte alternatif si fourni
    if (postData.altText && postData.altText.trim()) {
      formData.append('altText', postData.altText.trim());
    }

    try {
      let result;

      if (postData.postType === 'reel') {
        if (!postData.videoFile) {
          throw new Error('Aucun fichier vidéo sélectionné pour le Reel.');
        }

        // Si la génération automatique est activée et qu'un template est sélectionné
        if (postData.autoGenerate && selectedTemplate) {
          setPublishMessage('⏳ Génération de l\'overlay vidéo...');
          // Ajouter templateId et data au formData pour que le backend génère l'overlay
          formData.append('templateId', selectedTemplate.id);
          
          const templateData = { ...postData };
          if (Object.keys(customFontFamily).length > 0) {
            templateData.customFontFamily = customFontFamily;
          }
          if (Object.keys(customFontSize).length > 0) {
            templateData.customFontSize = customFontSize;
          }
          if (customTextColor) {
            templateData.customTextColor = customTextColor;
          }
          
          formData.append('data', JSON.stringify(templateData));
          formData.append('options', JSON.stringify({ overlayOpacity: 0.15, quality: 'medium' }));
        }

        formData.append('video', postData.videoFile);
        result = await apiService.publishReel(formData);
      } else if (postData.postType === 'carousel') {
        const filesToUpload = postData.autoGenerate 
          ? postData.generatedMedia.map(m => m.file)
          : postData.carouselMedia.map(m => m.file);
        
        if (filesToUpload.length === 0) {
          throw new Error('Aucun média à publier pour le carrousel.');
        }
        
        filesToUpload.forEach(file => formData.append('images', file));
        const sessionId = Date.now().toString();
        result = await apiService.publishCarousel(formData, sessionId);
      } else { // Cas 'post', 'story', 'landscape' ou 'portrait'
        let imageToUpload = postData.imageFile;

        // Si aucune image n'est prête, et qu'on est en mode auto-génération, on la crée.
        if (!imageToUpload && postData.autoGenerate) {
          setPublishMessage('⏳ L\'image n\'est pas prête, génération en cours...');
          imageToUpload = await handleAutoGenerateImage();
        }

        if (imageToUpload) {
          formData.append('image', imageToUpload);
          result = await apiService.publishPost(formData);
        } else {
          throw new Error('Aucune image à publier. Veuillez téléverser ou générer une image.');
        }
      }

      setPublishMessage(`✅ ${postData.postType.charAt(0).toUpperCase() + postData.postType.slice(1)} publié avec succès !`);
      
      // Scroll automatique vers la prévisualisation après publication réussie
      setTimeout(() => {
        previewSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      
      // ... (réinitialisation de l'état)
    } catch (error) {
      // ... (gestion d'erreur)
    } finally {
      setIsPublishing(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'published':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            ✅ Publié
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            ❌ Échoué
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            ⏳ En cours
          </span>
        );
    }
  };

  const getPostTypeBadge = (postType) => {
    const types = {
      'post': { label: 'Post', color: 'bg-blue-100 text-blue-800' },
      'story': { label: 'Story', color: 'bg-purple-100 text-purple-800' },
      'reel': { label: 'Reel', color: 'bg-pink-100 text-pink-800' },
      'carousel': { label: 'Carrousel', color: 'bg-orange-100 text-orange-800' }
    };
    const type = types[postType] || types.post;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${type.color}`}>
        {type.label}
      </span>
    );
  };

  const getUserName = (post) => {
    // Utiliser le nom de l'utilisateur depuis les données du post
    return post.user_name || `Utilisateur ${post.user_id}`;
  };

  const getUserAvatar = (post) => {
    // Utiliser l'avatar de l'utilisateur depuis les données du post
    return post.user_avatar || '👤';
  };

  const getApiStatusIcon = () => {
    switch (apiStatus) {
      case 'connected': 
        return <Wifi className="text-green-500" size={16} />;
      case 'disconnected': 
        return <WifiOff className="text-red-500" size={16} />;
      default: 
        return (
          <div className="animate-spin w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full"></div>
        );
    }
  };

  console.log('--- [DEBUG] Rendu du composant ---');
  console.log('Template sélectionné:', selectedTemplate ? selectedTemplate.name : 'Aucun');
  console.log('Type de post:', postData.postType);
  console.log('Images générées dans l\'état ?', postData.generatedMedia ? `Oui, ${postData.generatedMedia.length} images` : 'Non');
  console.log('------------------------------------');

  const publishButtonState = getPublishButtonState();

  const testClick = () => {
    console.log('%c --- TEST CLICK FONCTIONNE --- ', 'background: red; color: white; font-size: 20px;');
    handleAutoGenerateImage();
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <div className="text-2xl">📱</div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Instagram Team</h1>
                <p className="text-sm text-gray-600">@causecommune93.1</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {getApiStatusIcon()}
              <div className="flex items-center space-x-2">
                <div className="text-lg">{currentUser.avatar}</div>
                <span className="text-sm font-medium text-gray-700">{currentUser.name}</span>
              </div>
              
              {/* Indicateur de mot de passe par défaut */}
              {currentUser.email && !hasChangedDefaultPassword && (
                <div className="flex items-center space-x-2 bg-yellow-100 px-3 py-1 rounded-full">
                  <Settings size={14} className="text-yellow-600" />
                  <span className="text-xs text-yellow-800">Mot de passe par défaut</span>
                </div>
              )}
              
              <button
                onClick={handleLogout}
                className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {[
              { id: 'create', name: 'Créer', icon: Plus },
              { id: 'history', name: 'Historique', icon: Calendar },
              { id: 'profile', name: 'Profil', icon: User }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon size={16} />
                <span>{tab.name}</span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Contenu principal */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Notification de bienvenue */}
        {showWelcomeNotification && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <User size={24} className="text-blue-600" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-medium text-blue-900">
                  Bienvenue sur Instagram Team ! 👋
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    C'est votre première connexion ? Nous vous recommandons fortement de changer votre mot de passe par défaut pour sécuriser votre compte.
                  </p>
                </div>
                <div className="mt-4 flex space-x-3">
                  <button
                    onClick={() => {
                      setActiveTab('profile');
                      setShowWelcomeNotification(false);
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700"
                  >
                    Changer mon mot de passe
                  </button>
                  <button
                    onClick={() => setShowWelcomeNotification(false)}
                    className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md text-sm hover:bg-gray-300"
                  >
                    Plus tard
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'create' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Sélection de template */}
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Choisir un template</h2>
                {loadingTemplates ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
                    <span className="ml-3 text-gray-600">Chargement des templates...</span>
                  </div>
                ) : templates.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>Aucun template disponible</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    {templates.map(template => (
                      <button
                        key={template.id}
                        onClick={() => handleTemplateSelect(template)}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          selectedTemplate?.id === template.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div 
                          className="aspect-square w-full mb-2 rounded" 
                          style={{ background: template.style.background }}
                        ></div>
                        <div className="text-sm font-medium text-gray-900">{template.name}</div>
                        <div className="text-xs text-gray-600">
                          {template.format === 'story' ? 'Story' : 'Post'}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Formulaire de personnalisation */}
              {selectedTemplate && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Template : {selectedTemplate.name}
                    </h3>
                    <button
                      onClick={() => {
                        setSelectedTemplate(null);
                        setPostData({
                          ...postData,
                          template: null,
                          postType: 'post' // Revenir au type par défaut
                        });
                      }}
                      className="text-xs font-medium text-blue-600 hover:text-blue-800"
                    >
                      Changer
                    </button>
                  </div>
                  
                  {/* Sélecteur de type de publication */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type de publication
                    </label>
                    <select
                      value={postData.postType || 'post'}
                      onChange={(e) => {
                        // Permettre le changement uniquement si aucun template n'est sélectionné
                        if (!selectedTemplate) {
                          const newType = e.target.value;
                          setPostData({
                            ...postData,
                            postType: newType,
                            // Réinitialiser les images si on change de type
                            carouselImages: [],
                            imageFiles: [],
                            videoFile: null
                          });
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                      disabled={!!selectedTemplate} // Désactiver si un template est sélectionné
                    >
                      <option value="post">Post classique (carré 1:1)</option>
                      <option value="story">Story (vertical 9:16)</option>
                      <option value="reel">Reel (vidéo)</option>
                      <option value="carousel">Carrousel (images)</option>
                      <option value="landscape">Paysage (1.91:1)</option>
                      <option value="portrait">Portrait (4:5)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedTemplate 
                        ? "Le type est défini par le template. Pour changer, désélectionnez le template."
                        : "Choisissez un type ou sélectionnez un template."}
                    </p>
                  </div>
                  
                  {/* Mode génération automatique */}
                  {(postData.postType === 'post' || postData.postType === 'story' || postData.postType === 'carousel' || postData.postType === 'landscape' || postData.postType === 'portrait' || postData.postType === 'reel') && (
                    <div className="mt-4">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={postData.autoGenerate || false}
                          onChange={(e) => setPostData({
                            ...postData,
                            autoGenerate: e.target.checked
                          })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          Mode génération automatique ⚡
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        {postData.postType === 'carousel' 
                          ? 'Génère automatiquement les images du carrousel avec le template sélectionné'
                          : postData.postType === 'reel'
                          ? 'Génère automatiquement l\'overlay vidéo avec le template sélectionné'
                          : 'Génère automatiquement l\'image avec le template sélectionné'}
                      </p>
                    </div>
                  )}
                  
                  {/* Upload selon le type de publication */}
                  {postData.postType === 'reel' ? (
                    /* Upload video pour Reels */
                    <div>
                      {/* Message d'information spécifique aux Reels */}
                      {selectedTemplate && selectedTemplate.category === 'video' && (
                        <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-indigo-800 text-xs">
                          <p className="font-bold mb-1">ℹ️ Template Reel avec génération automatique</p>
                          <p>
                            {postData.autoGenerate 
                              ? 'L\'overlay (texte et design) sera automatiquement superposé sur votre vidéo lors de la publication.'
                              : 'Le template vous aide à structurer votre contenu. Activez la génération automatique pour superposer l\'overlay sur votre vidéo.'}
                          </p>
                        </div>
                      )}

                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Vidéo du Reel
                      </label>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleVideoUpload}
                        accept="video/*"
                        className="hidden"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors flex items-center justify-center space-x-2"
                      >
                        <Upload size={20} />
                        <span>{postData.videoFile ? 'Changer la vidéo' : 'Ajouter une vidéo'}</span>
                      </button>
                      {(postData.videoPreview || generatedVideoPreview) && (
                        <div className="mt-4">
                          <video 
                            src={generatedVideoPreview || postData.videoPreview} 
                            controls 
                            className="w-full max-w-xs mx-auto rounded-lg"
                            style={{ maxHeight: '200px' }}
                          />
                          {generatedVideoPreview && (
                            <p className="text-xs text-green-600 text-center mt-2">
                              ✅ Vidéo avec overlay générée
                            </p>
                          )}
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        Format vidéo accepté, max 100MB. Durée recommandée: 15-90 secondes.
                      </p>
                    </div>
                  ) : postData.postType === 'carousel' ? (
                    /* Upload multiple images pour Carrousels */
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Médias {
                          selectedTemplate?.carouselConfig
                          ? selectedTemplate.carouselConfig.minImages === selectedTemplate.carouselConfig.maxImages
                            ? `(exactement ${selectedTemplate.carouselConfig.maxImages})`
                            : `(${selectedTemplate.carouselConfig.minImages}-${selectedTemplate.carouselConfig.maxImages})`
                          : '(2-10 images)'
                        }
                      </label>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleCarouselImagesUpload}
                        accept="image/*,video/mp4,video/mov"
                        multiple
                        className="hidden"
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors flex items-center justify-center space-x-2"
                      >
                        <Upload size={20} />
                        <span>
                          {postData.carouselImages.length > 0 
                            ? `${postData.carouselImages.length} images sélectionnées` 
                            : 'Ajouter des images'
                          }
                        </span>
                      </button>
                      {postData.carouselMedia && postData.carouselMedia.length > 0 && (
                        <div className="mt-4 space-y-2">
                          {/* Bouton de réinitialisation si des images ont été générées */}
                          {postData.generatedMedia && (
                            <div className="flex justify-between items-center">
                              <button
                                onClick={() => {
                                  console.log('[DEBUG] État actuel:', {
                                    template: selectedTemplate,
                                    postData: postData,
                                    activeBlobURLs: activeBlobURLs.length
                                  });
                                }}
                                className="text-xs text-blue-600 hover:text-blue-800 underline"
                              >
                                🔍 Debug état
                              </button>
                              <button
                                onClick={resetCarouselGeneration}
                                className="text-sm text-red-600 hover:text-red-800 underline"
                              >
                                🗑️ Réinitialiser et régénérer
                              </button>
                            </div>
                          )}
                          
                          {/* Vignettes des images originales */}
                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                            {postData.carouselMedia.map((media, index) => (
                              <div key={index} className="relative group aspect-square">
                                {media.type === 'video' ? (
                                  <video 
                                    src={media.previewUrl} 
                                    className="w-full h-full object-cover rounded-lg bg-gray-900" 
                                    muted 
                                    playsInline 
                                    onMouseOver={e => e.target.play()} 
                                    onMouseOut={e => { e.target.pause(); e.target.currentTime = 0; }}
                                  />
                                ) : (
                                  <img 
                                    src={media.previewUrl} 
                                    alt={`Média ${index + 1}`} 
                                    className="w-full h-full object-cover rounded-lg"
                                  />
                                )}
                                
                                {media.type === 'video' && (
                                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="bg-black bg-opacity-50 rounded-full p-2">
                                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                  </div>
                                )}

                                <button
                                  onClick={() => removeCarouselImage(index)}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        {selectedTemplate && selectedTemplate.carouselConfig
                          ? selectedTemplate.carouselConfig.minImages === selectedTemplate.carouselConfig.maxImages
                            ? `Pour ce template, vous devez sélectionner exactement ${selectedTemplate.carouselConfig.maxImages} images.`
                            : `Ce template requiert entre ${selectedTemplate.carouselConfig.minImages} et ${selectedTemplate.carouselConfig.maxImages} images.`
                          : 'Sélectionnez entre 2 et 10 images.'}
                        {' '}Chaque image max 10MB.
                      </p>
                    </div>
                  ) : (
                    /* Upload image classique pour Posts et Stories */
                    <div className="space-y-4">
                      {/* L'ancien toggle pour la génération automatique était ici, il est maintenant supprimé. */}
                      {/* La génération est gérée par le state `postData.autoGenerate` unifié. */}

                      {/* On garde la logique d'upload si la génération auto n'est pas utilisée */}
                      {!postData.autoGenerate && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Image (upload manuel)
                          </label>
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageUpload}
                            accept="image/*"
                            className="hidden"
                          />
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors flex items-center justify-center space-x-2"
                          >
                            <Upload size={20} />
                            <span>{postData.image ? 'Changer l\'image' : 'Ajouter une image'}</span>
                          </button>
                          {postData.image && (
                            <div className="mt-4">
                              <img 
                                src={postData.image} 
                                alt="Aperçu"
                                className="w-full max-w-xs mx-auto rounded-lg"
                                style={{ maxHeight: '200px', objectFit: 'cover' }}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {/* Un message s'affiche si la génération auto est active, pour indiquer qu'il n'y a rien à uploader ici */}
                      {postData.autoGenerate && (
                         <div className="p-4 border-2 border-dashed border-blue-300 rounded-lg bg-blue-50 text-center">
                            <Zap size={32} className="mx-auto text-blue-600 mb-3" />
                            <h4 className="text-lg font-semibold text-blue-900 mb-2">
                              Génération automatique activée
                            </h4>
                            <p className="text-sm text-blue-700">
                              Remplissez les champs du template, l'image sera créée pour vous.
                            </p>
                         </div>
                      )}
                    </div>
                  )}

                  {/* Champs dynamiques basés sur le template */}
                  {selectedTemplate.fields.map((field) => (
                    <div key={field} className="space-y-1">
                      <label htmlFor={field} className="text-sm font-medium text-gray-700 capitalize">{field}</label>
                      <input
                        type="text"
                        id={field}
                        name={field}
                        value={postData[field] || ''}
                        onChange={(e) => setPostData({ ...postData, [field]: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        placeholder={`Entrez ${field}...`}
                      />
                    </div>
                  ))}

                  {/* Sélecteur de couleur du texte */}
                  <div className="space-y-1">
                    <label htmlFor="text-color" className="text-sm font-medium text-gray-700">
                      Couleur du texte
                    </label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        id="text-color"
                        value={customTextColor}
                        onChange={(e) => setCustomTextColor(e.target.value)}
                        className="w-16 h-10 border border-gray-300 rounded-md cursor-pointer"
                        title="Choisir la couleur du texte"
                      />
                      <input
                        type="text"
                        value={customTextColor}
                        onChange={(e) => setCustomTextColor(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        placeholder="#FFFFFF"
                        pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                      />
                      <button
                        type="button"
                        onClick={() => setCustomTextColor('#FFFFFF')}
                        className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
                        title="Réinitialiser à blanc"
                      >
                        Réinitialiser
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Choisissez la couleur du texte qui sera affiché sur votre image
                    </p>
                  </div>

                  {/* Contrôles de police et taille du texte par champ */}
                  <div className="space-y-3 border-t pt-3">
                    <h4 className="text-sm font-semibold text-gray-700">Style du texte par champ</h4>
                    <p className="text-xs text-gray-500">
                      Personnalisez la police et la taille pour chaque champ du template
                    </p>
                    
                    {selectedTemplate.fields.map((field) => (
                      <div key={`style-${field}`} className="space-y-2 p-3 bg-gray-50 rounded-lg border">
                        <label className="text-sm font-medium text-gray-700 capitalize">{field}</label>
                        
                        <div className="grid grid-cols-2 gap-3">
                          {/* Sélecteur de police pour ce champ */}
                          <div className="space-y-1">
                            <label htmlFor={`font-family-${field}`} className="text-xs text-gray-600">
                              Police
                            </label>
                            <select
                              id={`font-family-${field}`}
                              value={customFontFamily[field] || ''}
                              onChange={(e) => setCustomFontFamily({
                                ...customFontFamily,
                                [field]: e.target.value || undefined
                              })}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Par défaut</option>
                              <optgroup label="Google Fonts">
                                <option value="'Roboto', sans-serif">Roboto</option>
                                <option value="'Roboto Condensed', sans-serif">Roboto Condensed</option>
                                <option value="'Open Sans', sans-serif">Open Sans</option>
                                <option value="'Montserrat', sans-serif">Montserrat</option>
                                <option value="'Lato', sans-serif">Lato</option>
                                <option value="'Poppins', sans-serif">Poppins</option>
                                <option value="'Raleway', sans-serif">Raleway</option>
                                <option value="'Oswald', sans-serif">Oswald</option>
                                <option value="'Playfair Display', serif">Playfair Display</option>
                                <option value="'Merriweather', serif">Merriweather</option>
                              </optgroup>
                              <optgroup label="Polices système">
                                <option value="'Arial', sans-serif">Arial</option>
                                <option value="'Helvetica', sans-serif">Helvetica</option>
                                <option value="'Georgia', serif">Georgia</option>
                                <option value="'Times New Roman', serif">Times New Roman</option>
                                <option value="'Courier New', monospace">Courier New</option>
                              </optgroup>
                            </select>
                          </div>

                          {/* Sélecteur de taille pour ce champ */}
                          <div className="space-y-1">
                            <label htmlFor={`font-size-${field}`} className="text-xs text-gray-600">
                              Taille (px)
                            </label>
                            <div className="flex items-center space-x-1">
                              <input
                                type="number"
                                id={`font-size-${field}`}
                                min="12"
                                max="200"
                                value={customFontSize[field] || ''}
                                onChange={(e) => setCustomFontSize({
                                  ...customFontSize,
                                  [field]: e.target.value ? parseInt(e.target.value) : undefined
                                })}
                                className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Défaut"
                              />
                              {customFontSize[field] && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newSizes = { ...customFontSize };
                                    delete newSizes[field];
                                    setCustomFontSize(newSizes);
                                  }}
                                  className="px-2 py-1.5 text-xs text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
                                  title="Réinitialiser"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* === FIX: DEBUT - Ajout du champ pour l'image de fond (disponible en mode auto uniquement) === */}
                  {postData.autoGenerate && (
                    <div className="space-y-1">
                      <label htmlFor="background-image" className="text-sm font-medium text-gray-700">Image de fond (Optionnel)</label>
                      <div className="flex items-center space-x-2">
                        <button type="button" onClick={() => backgroundImageInputRef.current.click()} className="flex-grow justify-center inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                          <Upload className="w-4 h-4 mr-2" />
                          Choisir une image
                        </button>
                        <input
                          type="file"
                          id="background-image"
                          ref={backgroundImageInputRef}
                          onChange={handleBackgroundImageUpload}
                          className="hidden"
                          accept="image/jpeg, image/png"
                        />
                        {backgroundImage && (
                          <button onClick={removeBackgroundImage} className="text-red-500 hover:text-red-700 text-sm">Retirer</button>
                        )}
                      </div>
                      {backgroundImage && <p className="text-xs text-gray-500 mt-1 truncate">Fichier: {backgroundImageFile?.name}</p>}
                      <p className="text-xs text-gray-500 mt-1">Utilisez votre propre fond au lieu du gradient du template.</p>
                    </div>
                  )}
                  {/* === FIX: FIN === */}

                  {/* Caption Instagram */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Caption Instagram *
                    </label>
                    
                    {/* Encadré d'information sur les limitations Instagram */}
                    <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3 flex-1">
                          <h4 className="text-sm font-medium text-blue-900 mb-1">
                            Limitations Instagram
                          </h4>
                          <div className="text-xs text-blue-800 space-y-1">
                            <p className="flex items-center">
                              <span className="mr-2">✅</span>
                              <strong>Supporté :</strong> Emojis, sauts de ligne, hashtags (#), mentions (@), URLs
                            </p>
                            <p className="flex items-center">
                              <span className="mr-2">❌</span>
                              <strong>Non supporté :</strong> Gras, italique, souligné, couleurs (le formatage sera converti en texte brut)
                            </p>
                            <p className="text-blue-700 mt-2 italic">
                              💡 Astuce : Utilisez les sauts de ligne et les emojis pour structurer votre texte !
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <RichTextEditor
                      value={postData.caption}
                      onChange={(content) => setPostData({
                        ...postData,
                        caption: content
                      })}
                      placeholder="Rédigez votre caption..."
                      maxLength={2200}
                      className="w-full"
                    />
                  </div>

                  {/* Texte alternatif (ALT) pour l'accessibilité */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <span>Texte alternatif (ALT)</span>
                      <span className="ml-2 text-xs text-gray-500 font-normal">(Accessibilité)</span>
                    </label>
                    
                    {/* Encadré d'information sur le texte alternatif */}
                    <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="w-5 h-5 text-green-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3 flex-1">
                          <h4 className="text-sm font-medium text-green-900 mb-1">
                            Pourquoi ajouter un texte alternatif ?
                          </h4>
                          <div className="text-xs text-green-800 space-y-1">
                            <p>
                              Le texte alternatif permet aux personnes malvoyantes utilisant des lecteurs d'écran de comprendre le contenu de votre image. Il améliore l'accessibilité de vos publications Instagram.
                            </p>
                            <p className="text-green-700 mt-2 italic">
                              💡 Astuce : Décrivez brièvement et précisément ce que montre l'image, son contexte et les informations importantes qu'elle contient.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <textarea
                      value={postData.altText}
                      onChange={(e) => setPostData({
                        ...postData,
                        altText: e.target.value
                      })}
                      placeholder="Exemple : Affiche de l'émission Libre à Vous avec le titre 'Droit à l'oubli numérique' et la date du 15 janvier 2025"
                      maxLength={1000}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      {postData.altText.length}/1000 caractères
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Prévisualisation */}
            <div className="space-y-6" ref={previewSectionRef}>
              <h2 className="text-xl font-bold text-gray-900">Prévisualisation</h2>
              
              {selectedTemplate ? (
                <div className="space-y-4">
                  <div className="max-w-md mx-auto bg-gray-50 p-4 rounded-lg border">
                    {/* Afficher l'image générée si elle existe (avec texte) */}
                    {generatedImagePreview ? (
                      <div className="space-y-4">
                        <div className="text-sm text-gray-600 text-center">
                          Aperçu de l'image générée avec texte
                          <span className="ml-2 text-green-600">✅ Générée</span>
                        </div>
                        <div 
                          className="relative rounded-lg overflow-hidden mx-auto"
                          style={{
                            aspectRatio: selectedTemplate.format === 'story' ? '9/16' : 
                                        selectedTemplate.format === 'landscape' ? '1.91/1' :
                                        selectedTemplate.format === 'portrait' ? '4/5' : '1/1',
                            maxWidth: selectedTemplate.format === 'story' ? '300px' : 
                                     selectedTemplate.format === 'landscape' ? '600px' : '500px',
                            width: '100%'
                          }}
                        >
                          <img 
                            src={generatedImagePreview} 
                            alt="Aperçu de l'image générée" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </div>
                    ) : !postData.autoGenerate && postData.image ? (
                      /* Afficher l'image uploadée manuellement si elle existe mais n'a pas été générée avec texte */
                      <div className="space-y-4">
                        <div className="text-sm text-gray-600 text-center">
                          Aperçu de l'image uploadée
                          <span className="ml-2 text-blue-600">ℹ️ Cliquez sur "Générer avec texte" pour ajouter les textes du template</span>
                        </div>
                        <div 
                          className="relative rounded-lg overflow-hidden mx-auto"
                          style={{
                            aspectRatio: selectedTemplate.format === 'story' ? '9/16' : 
                                        selectedTemplate.format === 'landscape' ? '1.91/1' :
                                        selectedTemplate.format === 'portrait' ? '4/5' : '1/1',
                            maxWidth: selectedTemplate.format === 'story' ? '300px' : 
                                     selectedTemplate.format === 'landscape' ? '600px' : '500px',
                            width: '100%'
                          }}
                        >
                          <img 
                            src={postData.image} 
                            alt="Aperçu de l'image uploadée" 
                            className="w-full h-full object-contain"
                          />
                          <div className="absolute bottom-4 right-4 text-sm font-medium text-white z-20" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}>
                            @causecommune93.1
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Afficher le template avec les textes (mode auto ou pas d'image uploadée) */
                      <ImagePreview 
                        template={selectedTemplate} 
                        data={{
                          ...postData, 
                          customBg: backgroundImage,
                          customTextColor: customTextColor,
                          customFontFamily: Object.keys(customFontFamily).length > 0 ? customFontFamily : null,
                          customFontSize: Object.keys(customFontSize).length > 0 ? customFontSize : null
                        }}
                        currentCarouselIndex={currentCarouselIndex}
                        setCurrentCarouselIndex={setCurrentCarouselIndex}
                        onNavigate={null}
                      />
                    )}
                    
                    {/* Bouton de téléchargement pour les images générées */}
                    {(generatedImagePreview || postData.imageFile) && postData.postType !== 'carousel' && (
                      <div className="mt-4 flex justify-center">
                        <button
                          onClick={handleDownloadGenerated}
                          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Download size={16} />
                          <span>Télécharger l'image</span>
                        </button>
                      </div>
                    )}
                    
                    {/* Bouton de téléchargement pour les carrousels générés */}
                    {postData.generatedMedia && postData.generatedMedia.length > 0 && postData.postType === 'carousel' && (
                      <div className="mt-4 flex justify-center">
                        <button
                          onClick={handleDownloadCarouselImages}
                          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Download size={16} />
                          <span>Télécharger toutes les images ({postData.generatedMedia.length})</span>
                        </button>
                      </div>
                    )}

                    {/* Prévisualisation et téléchargement pour les vidéos générées (reels) */}
                    {postData.postType === 'reel' && generatedVideoPreview && (
                      <div className="mt-4 space-y-4">
                        <div className="text-sm text-gray-600 text-center">
                          Aperçu de la vidéo générée avec overlay
                          <span className="ml-2 text-green-600">✅ Générée</span>
                        </div>
                        <div className="max-w-md mx-auto">
                          <video 
                            src={generatedVideoPreview} 
                            controls 
                            className="w-full rounded-lg"
                            style={{ maxHeight: '400px' }}
                          />
                        </div>
                        <div className="flex justify-center">
                          <button
                            onClick={() => {
                              const link = document.createElement('a');
                              link.download = `generated-video-${Date.now()}.mp4`;
                              link.href = generatedVideoPreview;
                              link.click();
                            }}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <Download size={16} />
                            <span>Télécharger la vidéo</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Messages de statut */}
                  {publishMessage && (
                    <div className={`p-4 rounded-lg text-center ${
                      publishMessage.includes('succès') 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {publishMessage}
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="mt-6 space-y-3">
                    <div className="flex space-x-4">
                      
                      {/* === DEBUT DE LA CORRECTION : Logique de bouton clarifiée === */}
                      
                      {/* Bouton pour générer les images de CARROUSEL */}
                      {postData.autoGenerate && postData.postType === 'carousel' && (
                        <button
                          onClick={handleGenerateImages}
                          disabled={isGeneratingImage || isPublishing}
                          className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center space-x-2 disabled:bg-indigo-300"
                        >
                          <ImageIcon size={20} />
                          <span>Générer les images</span>
                        </button>
                      )}

                      {/* Bouton pour générer l'image d'un POST SIMPLE (mode auto) */}
                      {postData.autoGenerate && (postData.postType === 'post' || postData.postType === 'story' || postData.postType === 'landscape' || postData.postType === 'portrait') && (
                        <button
                          onClick={handleAutoGenerateImage}
                          disabled={isGeneratingImage || isPublishing}
                          className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center space-x-2 disabled:bg-indigo-300"
                        >
                          <Zap size={20} />
                          <span>Générer l'image avec texte</span>
                        </button>
                      )}

                      {/* Bouton pour générer l'image avec texte (mode upload manuel) */}
                      {!postData.autoGenerate && postData.image && (postData.postType === 'post' || postData.postType === 'story' || postData.postType === 'landscape' || postData.postType === 'portrait') && (
                        <button
                          onClick={handleAutoGenerateImage}
                          disabled={isGeneratingImage || isPublishing}
                          className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center space-x-2 disabled:bg-indigo-300"
                        >
                          <ImageIcon size={20} />
                          <span>Générer avec texte</span>
                        </button>
                      )}

                      {/* Bouton pour générer l'overlay vidéo pour les REELS */}
                      {postData.postType === 'reel' && selectedTemplate && postData.videoFile && (
                        <button
                          onClick={handleAutoGenerateVideo}
                          disabled={isGeneratingImage || isPublishing}
                          className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center space-x-2 disabled:bg-indigo-300"
                        >
                          {isGeneratingImage ? (
                            <>
                              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                              <span>Génération...</span>
                            </>
                          ) : (
                            <>
                              <Zap size={20} />
                              <span>Générer overlay vidéo</span>
                            </>
                          )}
                        </button>
                      )}
                      
                      {/* === FIN DE LA CORRECTION === */}
                      
                      <button
                        onClick={handlePublish}
                        disabled={isPublishing || publishButtonState.isDisabled}
                        className={`flex-1 px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2 ${
                          (isPublishing || publishButtonState.isDisabled)
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                      >
                        {isPublishing ? (
                          <>
                            <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                            <span>Publication...</span>
                          </>
                        ) : (
                          <>
                            <Send size={16} />
                            <span>Publier sur Instagram</span>
                          </>
                        )}
                      </button>
                    </div>
                    {/* Message d'aide pour le bouton de publication */}
                    {!isPublishing && publishButtonState.isDisabled && (
                      <p className="text-xs text-center text-red-600 font-medium">{publishButtonState.message}</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <ImageIcon size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Sélectionnez un template pour commencer</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">Historique des publications</h2>
              <button
                onClick={loadPostsHistory}
                disabled={loadingHistory}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
              >
                {loadingHistory ? (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                ) : (
                  <Calendar size={16} />
                )}
                <span>Actualiser</span>
              </button>
            </div>
            
            {loadingHistory ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-600">Chargement de l'historique...</p>
              </div>
            ) : postsHistory.length === 0 ? (
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 text-center text-gray-500">
                  <Calendar size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Aucune publication pour le moment</p>
                  <p className="text-sm">Les publications apparaîtront ici après avoir été créées</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {postsHistory.map((post) => (
                  <div key={post.id} className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <div className="text-2xl">{getUserAvatar(post)}</div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="font-medium text-gray-900">{getUserName(post)}</h3>
                            {getStatusBadge(post.status)}
                            {getPostTypeBadge(post.post_type)}
                          </div>
                          <p className="text-sm text-gray-600 mb-3">{formatDate(post.created_at)}</p>
                          {post.image_url && (
                            <div className="mb-4">
                              <img 
                                src={post.image_url} 
                                alt="Publication"
                                className="w-24 h-24 object-cover rounded-lg"
                              />
                            </div>
                          )}
                          <p className="text-gray-800 mb-3">{post.caption}</p>
                          {post.error && (
                            <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
                              <p className="text-sm text-red-800">
                                <strong>Erreur :</strong> {post.error}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {post.ig_post_id && (
                          <a
                            href={`https://instagram.com/p/${post.ig_post_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-1 px-3 py-1 bg-gradient-to-r from-purple-400 to-pink-400 text-white text-sm rounded-full hover:from-purple-500 hover:to-pink-500 transition-colors"
                          >
                            <ExternalLink size={14} />
                            <span>Instagram</span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                {postsHistory.length >= 50 && (
                  <div className="text-center py-4 text-gray-500">
                    <p className="text-sm">Affichage des 50 publications les plus récentes</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Profil utilisateur</h2>
              
              {/* Informations du profil - Section modifiable */}
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <UserCircle size={20} className="mr-2" />
                  Informations personnelles
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Avatar */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Avatar
                    </label>
                    <EmojiSelector
                      value={profileData.avatar}
                      onChange={(emoji) => setProfileData({ ...profileData, avatar: emoji })}
                      className="w-full max-w-xs"
                    />
                  </div>

                  {/* Nom complet */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      placeholder="Votre nom complet"
                      className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                        profileErrors.name ? 'border-red-300' : 'border-gray-300'
                      }`}
                      value={profileData.name}
                      onChange={(e) => {
                        setProfileData({ ...profileData, name: e.target.value });
                        if (profileErrors.name) {
                          setProfileErrors({ ...profileErrors, name: '' });
                        }
                      }}
                    />
                    {profileErrors.name && (
                      <p className="text-xs text-red-600 mt-1">{profileErrors.name}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Adresse email *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail size={16} className="text-gray-400" />
                      </div>
                      <input
                        type="email"
                        placeholder="votre.email@causecommune.fm"
                        className={`w-full pl-10 pr-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                          profileErrors.email ? 'border-red-300' : 'border-gray-300'
                        }`}
                        value={profileData.email}
                        onChange={(e) => {
                          setProfileData({ ...profileData, email: e.target.value });
                          if (profileErrors.email) {
                            setProfileErrors({ ...profileErrors, email: '' });
                          }
                        }}
                      />
                    </div>
                    {profileErrors.email && (
                      <p className="text-xs text-red-600 mt-1">{profileErrors.email}</p>
                    )}
                  </div>

                  {/* Rôle/Émission */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rôle / Émission *
                    </label>
                    <input
                      type="text"
                      placeholder="Ex: Animateur, Journaliste, Technicien, The Noise..."
                      className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                        profileErrors.role ? 'border-red-300' : 'border-gray-300'
                      }`}
                      value={profileData.role}
                      onChange={(e) => {
                        setProfileData({ ...profileData, role: e.target.value });
                        if (profileErrors.role) {
                          setProfileErrors({ ...profileErrors, role: '' });
                        }
                      }}
                    />
                    {profileErrors.role && (
                      <p className="text-xs text-red-600 mt-1">{profileErrors.role}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Indiquez votre rôle à la radio ou l'émission que vous animez
                    </p>
                  </div>
                </div>

                {/* Messages d'état */}
                {profileMessage && (
                  <div className={`mt-4 p-4 rounded-lg text-center ${
                    profileMessage.includes('succès') 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {profileMessage}
                  </div>
                )}

                {/* Boutons d'action */}
                <div className="flex space-x-3 pt-6 border-t border-gray-200 mt-6">
                  <button
                    onClick={handleUpdateProfile}
                    disabled={isUpdatingProfile}
                    className={`flex items-center space-x-2 px-6 py-2 rounded-lg transition-colors ${
                      isUpdatingProfile
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {isUpdatingProfile ? (
                      <>
                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                        <span>Mise à jour...</span>
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        <span>Sauvegarder</span>
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={handleResetProfile}
                    disabled={isUpdatingProfile}
                    className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Annuler
                  </button>
                </div>
              </div>
              
              {/* Section changement de mot de passe */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">🔒 Sécurité du compte</h3>
                
                {/* Message d'information */}
                {!hasChangedDefaultPassword && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <Settings size={20} className="text-yellow-600" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Changement de mot de passe recommandé
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>
                            Votre compte utilise actuellement un mot de passe par défaut. 
                            Il est fortement recommandé de le changer pour sécuriser votre compte.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mot de passe actuel *
                    </label>
                    <input
                      type="password"
                      placeholder="Entrez votre mot de passe actuel"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({
                        ...passwordData,
                        currentPassword: e.target.value
                      })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nouveau mot de passe *
                    </label>
                    <input
                      type="password"
                      placeholder="Entrez votre nouveau mot de passe"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({
                        ...passwordData,
                        newPassword: e.target.value
                      })}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Le mot de passe doit contenir au moins 8 caractères
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirmer le nouveau mot de passe *
                    </label>
                    <input
                      type="password"
                      placeholder="Confirmez le nouveau mot de passe"
                      className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                        passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword
                          ? 'border-red-300' 
                          : 'border-gray-300'
                      }`}
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({
                        ...passwordData,
                        confirmPassword: e.target.value
                      })}
                    />
                    {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                      <p className="text-xs text-red-600 mt-1">
                        Les mots de passe ne correspondent pas
                      </p>
                    )}
                  </div>

                  {/* Messages d'état */}
                  {passwordMessage && (
                    <div className={`p-4 rounded-lg text-center ${
                      passwordMessage.includes('succès') 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {passwordMessage}
                    </div>
                  )}

                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={handleChangePassword}
                      disabled={
                        isChangingPassword || 
                        !passwordData.currentPassword || 
                        !passwordData.newPassword || 
                        !passwordData.confirmPassword || 
                        passwordData.newPassword !== passwordData.confirmPassword
                      }
                      className={`flex items-center space-x-2 px-6 py-2 rounded-lg transition-colors ${
                        isChangingPassword || 
                        !passwordData.currentPassword || 
                        !passwordData.newPassword || 
                        !passwordData.confirmPassword || 
                        passwordData.newPassword !== passwordData.confirmPassword
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {isChangingPassword ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                          <span>Changement...</span>
                        </>
                      ) : (
                        <>
                          <Settings size={16} />
                          <span>Changer le mot de passe</span>
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={() => {
                        setPasswordData({
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: ''
                        });
                        setPasswordMessage('');
                      }}
                      className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default InstagramTeamApp;